import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState, type ClipboardEvent } from 'react'
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core'
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { api, type WorkItem, type WorkKind, type WorkReviewState, type WorkSide, type WorkStatus } from '../lib/api'
import { useCurrentUser, isAdminRole, isSuperAdminRole } from '../lib/auth'
import { Icon } from '../components/layout/Icon'
import {
  CUSTOMER_NAME,
  WORK_AGENT_KEY,
  WORK_BADGE_KEY,
  WORK_KINDS,
  WORK_LIST_KEY,
  WORK_PLACES,
  WORK_PRIORITIES,
  WORK_STATUSES,
  fmtWhen,
  imagesFromClipboard,
  kindMeta,
  priorityMeta,
  shrinkImage,
  sideGenitive,
  statusMeta,
} from '../lib/work'

type View = 'opin' | 'lokid' | 'oll'
type Search = { view?: View; ny?: boolean; kind?: WorkKind; status?: WorkStatus }

export const Route = createFileRoute('/verkefni')({
  component: WorkListPage,
  validateSearch: (s: Record<string, unknown>): Search => ({
    view: s.view === 'lokid' || s.view === 'oll' ? s.view : undefined,
    ny: s.ny === true || s.ny === 'true' ? true : undefined,
    kind: WORK_KINDS.some((k) => k.key === s.kind) ? (s.kind as WorkKind) : undefined,
    status: WORK_STATUSES.some((k) => k.key === s.status) ? (s.status as WorkStatus) : undefined,
  }),
})

const primaryBtn =
  'rounded-[11px] bg-[var(--brand)] px-4 py-2 text-sm font-semibold text-[var(--on-brand)] transition hover:bg-[var(--brand-strong)] disabled:cursor-not-allowed disabled:opacity-40'
const ghostBtn = 'rounded-[11px] px-3 py-2 text-sm font-medium text-[var(--muted)] transition hover:bg-[var(--surface-2)] hover:text-[var(--fg)]'
const card = 'rounded-[14px] border border-[var(--border)] bg-[var(--surface)]'

function WorkListPage() {
  const qc = useQueryClient()
  const { data: me, isLoading: meLoading } = useCurrentUser()
  const admin = isAdminRole(me)
  const search = Route.useSearch()
  const navigate = useNavigate()
  const { data, isLoading, error } = useQuery({ queryKey: WORK_LIST_KEY, queryFn: () => api.work.list(), enabled: admin, refetchInterval: 60_000 })

  const view: View = search.view ?? 'opin'
  const setSearch = (next: Partial<Search>) => navigate({ to: '/verkefni', search: { ...search, ...next }, replace: true })

  const items = useMemo(() => data?.items ?? [], [data])
  const side: WorkSide = data?.side ?? 'customer'
  const rows = useMemo(
    () =>
      items.filter((i) => {
        const open = statusMeta(i.status).open
        if (view === 'opin' && !open) return false
        if (view === 'lokid' && open) return false
        if (search.kind && i.kind !== search.kind) return false
        if (search.status && i.status !== search.status) return false
        return true
      }),
    [items, view, search.kind, search.status],
  )
  const openCount = items.filter((i) => statusMeta(i.status).open).length
  const mine = items.filter((i) => i.unread && statusMeta(i.status).open).length
  const deciding = items.filter((i) => i.review === 'decide' && statusMeta(i.status).open).length
  const canReorder = side === 'customer' && view === 'opin' && !search.kind && !search.status

  const reorder = useMutation({ mutationFn: (numbers: number[]) => api.work.reorder(numbers), onSettled: () => qc.invalidateQueries({ queryKey: WORK_LIST_KEY }) })
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))

  function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e
    if (!over || active.id === over.id || !data) return
    const open = items.filter((i) => statusMeta(i.status).open)
    const oldIndex = open.findIndex((i) => i.number === active.id)
    const newIndex = open.findIndex((i) => i.number === over.id)
    if (oldIndex < 0 || newIndex < 0) return
    const next = arrayMove(open, oldIndex, newIndex).map((it, idx) => ({ ...it, rank: idx }))
    const closed = items.filter((i) => !statusMeta(i.status).open)
    qc.setQueryData(WORK_LIST_KEY, { ...data, items: [...next, ...closed] })
    reorder.mutate(next.map((i) => i.number))
  }

  if (meLoading) return <p className="p-8 text-[var(--muted)]">Hleð…</p>
  if (!admin)
    return (
      <div className="mx-auto mt-10 max-w-xl rounded-[14px] border border-[var(--border)] bg-[var(--surface)] p-6">
        <h2 className="font-semibold text-[var(--fg)]">Enginn aðgangur</h2>
        <p className="mt-1 text-sm text-[var(--muted)]">Þú þarft stjórnandaaðgang til að skoða verkefnalistann.</p>
      </div>
    )

  return (
    <div className="max-w-[1240px] mx-auto px-6 sm:px-10 py-8 pb-16 igf-fade flex flex-col gap-6">
      <div className="flex items-end justify-between gap-5 flex-wrap">
        <div>
          <h1 className="font-grotesk font-bold text-[30px] leading-tight text-[var(--fg)] tracking-tight">Verkefnalisti</h1>
          <p className="mt-2 text-[15px] font-medium text-[var(--muted)] max-w-2xl">
            {isLoading
              ? 'Hleð…'
              : mine > 0
                ? `${mine} ${mine === 1 ? 'beiðni bíður' : 'beiðnir bíða'} svars frá þér · ${openCount} opnar`
                : `${openCount} ${openCount === 1 ? 'opin beiðni' : 'opnar beiðnir'} · ekkert bíður þín`}
            {deciding > 0 && (
              <span className="font-semibold text-[#dc2626]">
                {' '}
                · {deciding} {deciding === 1 ? 'bíður' : 'bíða'} Go / No go
              </span>
            )}
          </p>
        </div>
        <button type="button" onClick={() => setSearch({ ny: true })} className={primaryBtn}>
          Ný beiðni
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-[var(--border)]">
        {(
          [
            { id: 'opin', label: 'Opnar', count: openCount },
            { id: 'lokid', label: 'Lokaðar', count: items.length - openCount },
            { id: 'oll', label: 'Allar', count: items.length },
          ] as { id: View; label: string; count: number }[]
        ).map((t) => {
          const on = view === t.id
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setSearch({ view: t.id === 'opin' ? undefined : t.id })}
              className={`-mb-px border-b-2 px-3 py-2 text-sm font-semibold transition ${on ? 'border-[var(--brand)] text-[var(--brand)]' : 'border-transparent text-[var(--muted)] hover:text-[var(--fg)]'}`}
            >
              {t.label} <span className={on ? 'text-[var(--brand)]' : 'text-[var(--faint)]'}>{t.count}</span>
            </button>
          )
        })}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {WORK_KINDS.map((k) => {
          const n = items.filter((i) => i.kind === k.key && (view === 'oll' || statusMeta(i.status).open === (view === 'opin'))).length
          const on = search.kind === k.key
          if (n === 0 && !on) return null
          return (
            <button
              key={k.key}
              type="button"
              aria-pressed={on}
              onClick={() => setSearch({ kind: on ? undefined : k.key })}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium transition ${on ? 'border-[var(--brand)] bg-[var(--brand-soft)] text-[var(--brand)]' : 'border-[var(--border)] bg-[var(--surface)] text-[var(--muted)] hover:border-[var(--border-strong)]'}`}
            >
              <span aria-hidden>{k.icon}</span> {k.label} <span className={on ? 'text-[var(--brand)]' : 'text-[var(--faint)]'}>{n}</span>
            </button>
          )
        })}
        {search.status && (
          <button
            type="button"
            onClick={() => setSearch({ status: undefined })}
            className="inline-flex items-center gap-1.5 rounded-full border border-[var(--brand)] bg-[var(--surface)] px-3 py-1 text-sm text-[var(--brand)]"
          >
            Staða: {statusMeta(search.status).label} <span aria-hidden>✕</span>
          </button>
        )}
        {view === 'opin' && openCount > 1 && (
          <span className="ml-auto text-xs text-[var(--muted)]">
            {side === 'customer'
              ? canReorder
                ? 'Dragðu beiðnirnar í þá röð sem þið viljið fá þær — Laufið sér röðina.'
                : 'Taktu síuna af til að raða.'
              : `Röðin er sú sem ${CUSTOMER_NAME} bað um.`}
          </span>
        )}
      </div>

      {reorder.error instanceof Error && (
        <p className="text-sm text-[#dc2626]">Röðin vistaðist ekki og listinn sýnir fyrri röð: {reorder.error.message}</p>
      )}

      {error instanceof Error ? (
        <div className="rounded-[14px] border border-[#dc2626]/30 bg-[rgba(220,38,38,.08)] p-4 text-sm text-[#dc2626]">Náði ekki í listann: {error.message}</div>
      ) : isLoading ? (
        <p className="text-[var(--muted)]">Hleð…</p>
      ) : rows.length === 0 ? (
        <div className="rounded-[14px] border border-dashed border-[var(--border-strong)] bg-[var(--surface)] p-10 text-center">
          <p className="text-sm text-[var(--fg-soft)]">{items.length === 0 ? 'Engin beiðni skráð enn.' : 'Engin beiðni passar við síuna.'}</p>
          <p className="mt-1 text-sm text-[var(--muted)]">
            {items.length === 0
              ? 'Sástu eitthvað sem er bilað, pirrandi eða vantar? Smelltu á „Ný beiðni“ og lýstu því — hver beiðni fær númer sem hægt er að vísa í.'
              : 'Breyttu síunni hér fyrir ofan eða skoðaðu „Allar“.'}
          </p>
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={rows.map((i) => i.number)} strategy={verticalListSortingStrategy}>
            <ul className={`overflow-hidden ${card}`}>
              {rows.map((i, idx) => (
                <Row key={i.id} item={i} side={side} draggable={canReorder} position={view === 'opin' && !search.kind && !search.status ? idx + 1 : null} />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}

      {isSuperAdminRole(me) && <AgentBox />}

      {search.ny && <NewItemDrawer onClose={() => setSearch({ ny: undefined })} />}
    </div>
  )
}

function Row({ item: i, side, draggable, position }: { item: WorkItem; side: WorkSide; draggable: boolean; position: number | null }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: i.number, disabled: !draggable })
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 }
  return (
    <li ref={setNodeRef} style={style} className="border-b border-[var(--border)] last:border-b-0">
      <div className={`flex items-start gap-3 px-4 py-3 transition hover:bg-[var(--surface-2)] ${i.unread ? 'bg-[var(--brand-soft)]/40' : ''}`}>
        {draggable && (
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="mt-0.5 cursor-grab touch-none text-[var(--faint)] hover:text-[var(--fg)]"
            title="Dragðu til að raða"
            aria-label="Dragðu til að raða"
          >
            <Icon name="grip" className="h-4 w-4" />
          </button>
        )}
        {position !== null && <span className="w-6 flex-none pt-0.5 text-right text-sm font-semibold tabular-nums text-[var(--faint)]">{position}.</span>}
        <Link to="/verkefni/$number" params={{ number: String(i.number) }} className="flex min-w-0 flex-1 items-start gap-4">
          <span className="w-12 flex-none pt-0.5 font-mono text-sm text-[var(--faint)]">#{i.number}</span>
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-2">
              <span className={`truncate text-sm ${i.unread ? 'font-semibold text-[var(--fg)]' : 'font-medium text-[var(--fg-soft)]'}`}>{i.title}</span>
              {i.unread && <span className="inline-flex h-2 w-2 flex-none rounded-full bg-[#dc2626]" title="Bíður svars frá þér" />}
            </span>
            <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[var(--muted)]">
              <span>
                <span aria-hidden>{kindMeta(i.kind).icon}</span> {kindMeta(i.kind).label}
              </span>
              <PriorityDots p={i.priority} />
              <span>{i.createdByName}</span>
              <span>{fmtWhen(i.activityAt)}</span>
              {i.messageCount > 0 && <span>💬 {i.messageCount}</span>}
              {i.attachmentCount > 0 && <span>🖼 {i.attachmentCount}</span>}
              {i.awaitingSide !== side && statusMeta(i.status).open && <span className="text-[var(--faint)]">· bíður {sideGenitive(i.awaitingSide)}</span>}
            </span>
          </span>
          <span className="flex flex-none flex-col items-end gap-1">
            <StatusBadge status={i.status} />
            {side === 'team' && i.review && statusMeta(i.status).open && <ReviewChip state={i.review} />}
          </span>
        </Link>
      </div>
    </li>
  )
}

const REVIEW_META: Record<WorkReviewState, { label: string; bg: string; color: string }> = {
  pending: { label: 'Í mati hjá Björk', bg: 'var(--acc-amber-soft)', color: 'var(--acc-amber)' },
  decide: { label: 'Go / No go?', bg: 'rgba(220,38,38,.12)', color: '#dc2626' },
  go: { label: 'Go', bg: 'var(--acc-green-soft)', color: 'var(--acc-green)' },
  nogo: { label: 'No go', bg: 'var(--surface-2)', color: 'var(--muted)' },
}

function ReviewChip({ state }: { state: WorkReviewState }) {
  const m = REVIEW_META[state]
  return (
    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ background: m.bg, color: m.color }}>
      {m.label}
    </span>
  )
}

export function StatusBadge({ status }: { status: WorkStatus }) {
  const m = statusMeta(status)
  return (
    <span className="inline-flex flex-none items-center rounded-full px-2.5 py-0.5 text-xs font-semibold" style={{ background: m.bg, color: m.color }}>
      {m.label}
    </span>
  )
}

export function PriorityDots({ p }: { p: number }) {
  const m = priorityMeta(p)
  const color = p === 3 ? '#dc2626' : p === 2 ? 'var(--acc-amber)' : 'var(--faint)'
  return (
    <span className="inline-flex items-center gap-1" title={`Mikilvægi: ${m.label}`}>
      {[1, 2, 3].map((n) => (
        <span key={n} className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: n <= p ? color : 'var(--border-strong)' }} />
      ))}
      <span>{m.label}</span>
    </span>
  )
}

function AgentBox() {
  const qc = useQueryClient()
  const { data, error } = useQuery({ queryKey: WORK_AGENT_KEY, queryFn: () => api.work.agent(), refetchInterval: 60_000 })
  const [shown, setShown] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const create = useMutation({
    mutationFn: () => api.work.createAgentKey(),
    onSuccess: (r) => {
      setShown(r.key)
      qc.setQueryData(WORK_AGENT_KEY, r.status)
    },
  })
  const revoke = useMutation({
    mutationFn: () => api.work.revokeAgentKey(),
    onSuccess: (s) => {
      setShown(null)
      qc.setQueryData(WORK_AGENT_KEY, s)
    },
  })
  const seen = data?.lastSeenAt ? new Date(data.lastSeenAt) : null
  const quiet = seen ? Date.now() - seen.getTime() > 2 * 60 * 60_000 : true

  return (
    <section className={`${card} p-5 text-sm`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold text-[var(--fg)]">Verkefnavaktin</h2>
          <p className="mt-0.5 text-xs text-[var(--muted)]">
            Björk les nýjar beiðnir frá {CUSTOMER_NAME}, metur þær og sendir þér push, SMS og póst. Aðeins superadmin sér þennan reit.
          </p>
        </div>
        {error instanceof Error ? (
          <span className="text-xs text-[#dc2626]">{error.message}</span>
        ) : !data ? null : !data.hasKey ? (
          <span className="rounded-full bg-[var(--surface-2)] px-2.5 py-0.5 text-xs font-medium text-[var(--muted)]">Ekki tengd</span>
        ) : (
          <span
            className="rounded-full px-2.5 py-0.5 text-xs font-medium"
            style={quiet ? { background: 'var(--acc-amber-soft)', color: 'var(--acc-amber)' } : { background: 'var(--acc-green-soft)', color: 'var(--acc-green)' }}
          >
            {seen ? `Leit síðast við ${fmtWhen(seen.toISOString())}` : 'Hefur ekki litið við enn'}
          </span>
        )}
      </div>
      {shown && (
        <div className="mt-3 rounded-[11px] p-3" style={{ background: 'var(--acc-amber-soft)' }}>
          <p className="text-xs text-[var(--fg)]">Lykillinn birtist aðeins núna. Settu hann í húsið þar sem Björk segir til; ef hann týnist býrðu til nýjan.</p>
          <div className="mt-2 flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded bg-[var(--surface)] px-2 py-1 font-mono text-xs text-[var(--fg)]">{shown}</code>
            <button
              type="button"
              onClick={() =>
                void navigator.clipboard.writeText(shown).then(() => {
                  setCopied(true)
                  window.setTimeout(() => setCopied(false), 2000)
                })
              }
              className="rounded-[11px] border border-[var(--border-strong)] bg-[var(--surface)] px-3 py-1 text-xs font-medium text-[var(--fg-soft)] hover:border-[var(--brand)]"
            >
              {copied ? 'Afritað ✓' : 'Afrita'}
            </button>
          </div>
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={create.isPending}
          onClick={() => {
            if (!data?.hasKey || window.confirm('Búa til nýjan lykil?\n\nGamli lykillinn hættir að virka um leið. Björk þarf þann nýja.')) create.mutate()
          }}
          className="rounded-[11px] bg-[var(--brand)] px-3 py-1.5 text-xs font-semibold text-[var(--on-brand)] hover:bg-[var(--brand-strong)] disabled:opacity-40"
        >
          {data?.hasKey ? 'Nýr lykill' : 'Tengja vaktina'}
        </button>
        {data?.hasKey && (
          <button
            type="button"
            disabled={revoke.isPending}
            onClick={() => {
              if (window.confirm('Aftengja vaktina?\n\nBjörk hættir að sjá beiðnir þar til nýr lykill er búinn til.')) revoke.mutate()
            }}
            className="rounded-[11px] border border-[var(--border-strong)] px-3 py-1.5 text-xs font-medium text-[var(--fg-soft)] hover:border-[#dc2626] hover:text-[#dc2626]"
          >
            Aftengja
          </button>
        )}
        {(create.error ?? revoke.error) instanceof Error && <span className="text-xs text-[#dc2626]">{(create.error ?? revoke.error)?.message}</span>}
      </div>
    </section>
  )
}

const inputCls =
  'w-full rounded-[11px] border border-[var(--border-strong)] bg-[var(--surface)] px-3 py-2 text-sm text-[var(--fg)] outline-none transition focus:border-[var(--brand)]'

export function PlaceInput({ value, onChange, onBlur }: { value: string; onChange: (v: string) => void; onBlur?: () => void }) {
  return (
    <>
      <input list="igf-work-places" value={value} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} placeholder="t.d. Fyrirtæki › Kostnaður" className={inputCls} maxLength={300} />
      <datalist id="igf-work-places">
        {WORK_PLACES.map((p) => (
          <option key={p} value={p} />
        ))}
      </datalist>
    </>
  )
}

function NewItemDrawer({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [kind, setKind] = useState<WorkKind | null>(null)
  const [priority, setPriority] = useState<1 | 2 | 3>(2)
  const [where, setWhere] = useState('')
  const [shots, setShots] = useState<{ blob: Blob; url: string }[]>([])
  const [shotError, setShotError] = useState<string | null>(null)
  const [created, setCreated] = useState<number | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const shotsRef = useRef(shots)

  useEffect(() => {
    shotsRef.current = shots
  }, [shots])
  useEffect(() => () => shotsRef.current.forEach((s) => URL.revokeObjectURL(s.url)), [])

  const dirty = created === null ? title.trim().length > 0 || description.trim().length > 0 || shots.length > 0 : shots.length > 0
  const requestClose = useCallback(() => {
    if (dirty && !window.confirm(created === null ? 'Loka án þess að senda?\n\nBeiðnin sem þú ert að skrifa glatast.' : `Loka?\n\nBeiðni #${created} er vistuð, en myndirnar sem komust ekki upp glatast.`)) return
    onClose()
  }, [dirty, created, onClose])

  const removeShot = (shot: { blob: Blob; url: string }) => {
    URL.revokeObjectURL(shot.url)
    setShots((x) => x.filter((y) => y !== shot))
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [requestClose])

  const addFiles = async (files: Iterable<File | Blob>) => {
    setShotError(null)
    for (const f of files) {
      try {
        const blob = await shrinkImage(f)
        setShots((s) => [...s, { blob, url: URL.createObjectURL(blob) }])
      } catch (e) {
        setShotError(e instanceof Error ? e.message : 'Myndin komst ekki inn.')
      }
    }
  }
  const onPaste = (e: ClipboardEvent) => {
    const imgs = imagesFromClipboard(e.clipboardData)
    if (imgs.length > 0) {
      e.preventDefault()
      void addFiles(imgs)
    }
  }

  const save = useMutation({
    mutationFn: async () => {
      let number = created
      if (number === null) {
        const detail = await api.work.create({ title: title.trim(), description: description.trim(), kind: kind!, priority, where: where.trim() || null })
        number = detail.item.number
        setCreated(number)
        qc.invalidateQueries({ queryKey: WORK_LIST_KEY })
        qc.invalidateQueries({ queryKey: WORK_BADGE_KEY })
      }
      const failed: string[] = []
      for (const s of shots) {
        try {
          await api.work.attach(number, s.blob)
          removeShot(s)
        } catch (e) {
          failed.push(e instanceof Error ? e.message : 'óþekkt villa')
        }
      }
      if (failed.length > 0) {
        throw new Error(
          `Beiðni #${number} er vistuð, en ${failed.length === 1 ? 'ein mynd komst' : `${failed.length} myndir komust`} ekki upp (${failed[0]}). Reyndu aftur til að senda ${failed.length === 1 ? 'hana' : 'þær'}.`,
        )
      }
      return number
    },
    onSuccess: (number) => {
      qc.invalidateQueries({ queryKey: WORK_LIST_KEY })
      qc.invalidateQueries({ queryKey: WORK_BADGE_KEY })
      navigate({ to: '/verkefni/$number', params: { number: String(number) } })
    },
  })

  const canSave = (created !== null || (title.trim().length > 0 && description.trim().length > 0 && kind !== null)) && !save.isPending

  return (
    <div className="fixed inset-0 z-40" role="dialog" aria-modal="true" aria-label="Ný beiðni">
      <button type="button" aria-label="Loka" onClick={requestClose} className="absolute inset-0 h-full w-full cursor-default bg-black/30" />
      <div className="absolute inset-y-0 right-0 flex w-full max-w-2xl flex-col bg-[var(--bg)] shadow-[var(--shadow-lift)]">
        <div className="flex flex-none items-center justify-between border-b border-[var(--border)] bg-[var(--surface)] px-6 py-4">
          <div>
            <h2 className="text-lg font-bold tracking-tight text-[var(--fg)]">Ný beiðni</h2>
            <p className="text-xs text-[var(--muted)]">Hún fær númer um leið og hún er send, og hin hliðin sér hana strax.</p>
          </div>
          <button type="button" onClick={requestClose} className={ghostBtn}>
            Loka ✕
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-6" onPaste={onPaste}>
          <section className={`${card} p-5`}>
            <span className="mb-2 block text-sm font-semibold text-[var(--fg)]">Hvers konar beiðni er þetta?</span>
            <div className="grid gap-2 sm:grid-cols-2">
              {WORK_KINDS.map((k) => {
                const on = kind === k.key
                return (
                  <button
                    key={k.key}
                    type="button"
                    onClick={() => setKind(k.key)}
                    aria-pressed={on}
                    className={`flex items-start gap-3 rounded-[11px] border px-3 py-2.5 text-left transition ${on ? 'border-[var(--brand)] bg-[var(--brand-soft)]' : 'border-[var(--border)] bg-[var(--surface)] hover:border-[var(--border-strong)]'}`}
                  >
                    <span aria-hidden className={`mt-0.5 text-lg leading-none ${on ? 'text-[var(--brand)]' : 'text-[var(--faint)]'}`}>
                      {k.icon}
                    </span>
                    <span>
                      <span className={`block text-sm font-medium ${on ? 'text-[var(--brand)]' : 'text-[var(--fg)]'}`}>{k.label}</span>
                      <span className="block text-xs text-[var(--muted)]">{k.hint}</span>
                    </span>
                  </button>
                )
              })}
            </div>
          </section>

          <section className={`${card} space-y-4 p-5`}>
            <label className="block">
              <span className="mb-1.5 block text-sm font-semibold text-[var(--fg)]">Heiti</span>
              <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ein setning sem segir hvað þetta er" className={inputCls} maxLength={200} />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-sm font-semibold text-[var(--fg)]">Lýsing</span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={6}
                placeholder={'Hvað gerðist, hvað bjóstu við, og hvernig komst þú þangað?\nSkjámynd má líma beint hér inn (Ctrl+V).'}
                className={inputCls}
                maxLength={8000}
              />
            </label>
            <div className="block">
              <span className="mb-1.5 block text-sm font-semibold text-[var(--fg)]">
                Hvar í kerfinu <span className="font-normal text-[var(--faint)]">(valfrjálst)</span>
              </span>
              <PlaceInput value={where} onChange={setWhere} />
            </div>
          </section>

          <section className={`${card} p-5`}>
            <span className="mb-2 block text-sm font-semibold text-[var(--fg)]">Hversu mikilvægt?</span>
            <div className="flex flex-wrap gap-2">
              {WORK_PRIORITIES.map((p) => {
                const on = priority === p.key
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setPriority(p.key)}
                    aria-pressed={on}
                    className={`rounded-[11px] border px-3 py-2 text-left transition ${on ? 'border-[var(--brand)] bg-[var(--brand-soft)]' : 'border-[var(--border)] bg-[var(--surface)] hover:border-[var(--border-strong)]'}`}
                  >
                    <span className={`block text-sm font-medium ${on ? 'text-[var(--brand)]' : 'text-[var(--fg)]'}`}>{p.label}</span>
                    <span className="block text-xs text-[var(--muted)]">{p.hint}</span>
                  </button>
                )
              })}
            </div>
          </section>

          <section className={`${card} p-5`}>
            <div className="mb-2 flex items-center justify-between gap-3">
              <span className="text-sm font-semibold text-[var(--fg)]">
                Skjámyndir <span className="font-normal text-[var(--faint)]">(valfrjálst)</span>
              </span>
              <button type="button" onClick={() => fileRef.current?.click()} className="text-sm font-medium text-[var(--brand)] hover:underline">
                Velja mynd…
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  const files = [...(e.target.files ?? [])]
                  e.target.value = ''
                  void addFiles(files)
                }}
              />
            </div>
            {shots.length === 0 ? (
              <p className="text-xs text-[var(--muted)]">Límdu skjámynd hvar sem er í þessum glugga (Ctrl+V) eða veldu skrá. Mynd segir oft meira en lýsingin.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {shots.map((s) => (
                  <span key={s.url} className="relative">
                    <img src={s.url} alt="" className="h-24 rounded-[11px] border border-[var(--border)] object-cover" />
                    <button
                      type="button"
                      onClick={() => removeShot(s)}
                      aria-label="Fjarlægja mynd"
                      className="absolute -right-1.5 -top-1.5 h-5 w-5 rounded-full bg-[var(--fg)] text-xs text-[var(--surface)]"
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}
            {shotError && <p className="mt-2 text-xs text-[#dc2626]">{shotError}</p>}
          </section>
        </div>

        <div className="flex flex-none items-center justify-end gap-3 border-t border-[var(--border)] bg-[var(--surface)] px-6 py-4">
          {save.error instanceof Error && (
            <span className="mr-auto text-sm font-medium text-[#dc2626]">
              {save.error.message}
              {created !== null && (
                <>
                  {' '}
                  <Link to="/verkefni/$number" params={{ number: String(created) }} className="underline">
                    Opna #{created}
                  </Link>
                </>
              )}
            </span>
          )}
          <button type="button" onClick={requestClose} className={ghostBtn}>
            Hætta við
          </button>
          <button type="button" disabled={!canSave} onClick={() => save.mutate()} className={primaryBtn}>
            {save.isPending ? 'Sendi…' : created !== null ? 'Senda myndirnar aftur' : 'Senda beiðni'}
          </button>
        </div>
      </div>
    </div>
  )
}
